import { isValid } from "date-fns";

const FALLBACK_LABEL = "N/A";
const UNKNOWN_LOCATION = "Unknown location";

const isPresent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const asText = (value) => {
  if (!isPresent(value)) return null;
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  return null;
};

const readPath = (source, path) => {
  if (!source || !path) return undefined;

  return path.split(".").reduce((value, key) => {
    if (value === null || value === undefined) return undefined;
    return value[key];
  }, source);
};

export const toDisplayValue = (value, fallback = FALLBACK_LABEL) => {
  const text = asText(value);
  return text || fallback;
};

const isHexId = (value) =>
  typeof value === "string" && /^[0-9a-fA-F]{24}$/.test(value.trim());

export const getNestedName = (value, ridersMap = null) => {
  if (!value) return null;

  if (typeof value === "string" || typeof value === "number") {
    const text = asText(value);
    if (!text) return null;

    if (ridersMap && (isHexId(text) || ridersMap.has(text))) {
      const mappedName = ridersMap.get(text);
      if (mappedName) return mappedName;
    }

    if (isHexId(text)) return null;

    return text;
  }

  if (typeof value !== "object") return null;

  const directName = [
    value.name,
    value.fullName,
    value.displayName,
    value.riderName,
    value.driverName,
    value.acceptedByName,
    value.acceptedRiderName,
    value.acceptedDriverName,
    value.passengerName,
    value.customerName,
    value.user?.name,
    value.user?.fullName,
    value.user?.displayName,
    value.rider?.name,
    value.driver?.name,
    value.profile?.name,
  ]
    .map(asText)
    .filter((name) => name && !isHexId(name))
    .find(Boolean);

  if (directName) return directName;

  const firstName = asText(
    value.firstName || value.user?.firstName || value.profile?.firstName,
  );
  const lastName = asText(
    value.lastName || value.user?.lastName || value.profile?.lastName,
  );
  const combinedName = [firstName, lastName]
    .filter((part) => part && !isHexId(part))
    .join(" ");

  if (combinedName) return combinedName;

  if (value.nextOfKinName && asText(value.nextOfKinName)) {
    return `Rider (${asText(value.nextOfKinName)})`;
  }

  if (ridersMap) {
    const candidateIds = [
      value.id,
      value._id,
      value.riderId,
      value.driverId,
      value.userId,
      value.user?._id,
      value.user?.id,
      value.rider?._id,
      value.rider?.id,
      value.driver?._id,
      value.driver?.id,
    ]
      .map(asText)
      .filter(Boolean);

    for (const candidateId of candidateIds) {
      if (ridersMap.has(candidateId)) {
        return ridersMap.get(candidateId);
      }
    }
  }

  return null;
};

export const getPersonName = (
  primary,
  fallbackObjects = [],
  fallback = FALLBACK_LABEL,
  ridersMap = null,
) => {
  const candidates = [primary, ...fallbackObjects].filter(Boolean);
  for (const candidate of candidates) {
    const name = getNestedName(candidate, ridersMap);
    if (name) return name;
  }
  return fallback;
};

export const buildRidersMap = (ridersList = []) => {
  const map = new Map();
  if (!Array.isArray(ridersList)) return map;

  ridersList.forEach((rider) => {
    if (!rider || typeof rider !== "object") return;

    const riderName =
      rider.user?.name ||
      rider.name ||
      (rider.nextOfKinName ? `Rider (${rider.nextOfKinName})` : null);

    if (!riderName) return;

    const keys = [
      rider.id,
      rider._id,
      rider.riderId,
      rider.user?._id,
      rider.user?.id,
      rider.user?.riderId,
    ]
      .map(asText)
      .filter(Boolean);

    keys.forEach((key) => {
      map.set(key, riderName);
    });
  });

  return map;
};

export const getRiderRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.riders)) return payload.riders;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;

  return [];
};

export const getTimestamp = (source, candidates = []) => {
  for (const candidate of candidates) {
    const value = typeof candidate === "string" ? readPath(source, candidate) : candidate;
    if (isPresent(value)) return value;
  }
  return null;
};

const toDate = (value) => {
  if (!isPresent(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : null;
};

/**
 * Clock time for a trip checkpoint, always in Nigerian time.
 *
 * Two things this fixes. First the format: date-fns `h:mm a` renders
 * "7:35 PM", and what support actually reads out is "7:35pm" — lowercase, no
 * space. Second, and more important, the timezone. `format` uses whatever zone
 * the viewer's browser is in, so the same trip read differently depending on
 * where the admin happened to be sitting. Zooma operates in one city; a
 * checkpoint means the wall-clock time in Ilorin, and nothing else.
 */
const LAGOS_TZ = "Africa/Lagos";

const lagosTime = (date) =>
  new Intl.DateTimeFormat("en-NG", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: LAGOS_TZ,
  })
    .format(date)
    // en-NG yields "7:35 pm" or "7:35 PM" depending on the runtime's ICU data —
    // normalise both to the "7:35pm" the team reads.
    .replace(/\s?([ap])\.?m\.?/i, (_, meridiem) => meridiem.toLowerCase() + "m");

const lagosDate = (date) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: LAGOS_TZ,
  }).format(date);

export const formatTripDateTime = (value) => {
  const date = toDate(value);
  return date ? `${lagosDate(date)}, ${lagosTime(date)}` : FALLBACK_LABEL;
};

export const formatTripTime = (value) => {
  const date = toDate(value);
  return date ? lagosTime(date) : FALLBACK_LABEL;
};

// General Lagos-time formatters. Each screen passes the locale and options it
// already used, so only the zone changes, not the look.
export const formatLagos = (value, locale, options, fallback = FALLBACK_LABEL) => {
  const date = toDate(value);
  return date
    ? new Intl.DateTimeFormat(locale, { ...options, timeZone: LAGOS_TZ }).format(date)
    : fallback;
};

export const formatLagosDateTime = (
  value,
  locale = "en-GB",
  options = { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" },
  fallback,
) => formatLagos(value, locale, options, fallback);

export const formatLagosDate = (
  value,
  locale = "en-GB",
  options = { day: "numeric", month: "short", year: "numeric" },
  fallback,
) => formatLagos(value, locale, options, fallback);

export const formatLagosTime = (
  value,
  locale = "en-GB",
  options = { hour: "2-digit", minute: "2-digit" },
  fallback,
) => formatLagos(value, locale, options, fallback);

const lagosPartsFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  hourCycle: "h23",
  timeZone: LAGOS_TZ,
});

/**
 * The Lagos calendar date and hour of an instant: { year, month (1-12), day,
 * hour (0-23), weekday (0 = Sunday), dayNumber (days since 1970-01-01, for
 * week maths) }. Null for a missing or invalid value.
 */
export const lagosParts = (value = new Date()) => {
  const date = toDate(value);
  if (!date) return null;
  const parts = {};
  for (const { type, value: part } of lagosPartsFormat.formatToParts(date)) {
    if (type !== "literal") parts[type] = Number(part);
  }
  const dayNumber = Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000;
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    weekday: new Date(dayNumber * 86400000).getUTCDay(),
    dayNumber,
  };
};

export const lagosHour = (value = new Date()) => lagosParts(value)?.hour ?? null;

// "YYYY-MM-DD" of the Lagos calendar day.
export const lagosDayKey = (value = new Date()) => {
  const p = lagosParts(value);
  if (!p) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

const getNumericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;

  const numericValue = Number(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getNumericFromPaths = (source, paths) =>
  paths
    .map((path) => getNumericValue(readPath(source, path)))
    .find((value) => value !== null) ?? null;

const getMoneyNumber = (source, koboPaths, nairaPaths) => {
  const koboValue = getNumericFromPaths(source, koboPaths);
  if (koboValue !== null) return koboValue / 100;
  return getNumericFromPaths(source, nairaPaths);
};

const formatNaira = (amount) => {
  if (amount === null) return FALLBACK_LABEL;

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(amount);
};

export const getFareNumber = (booking = {}) =>
  getMoneyNumber(
    booking,
    [
      "fareKobo",
      "fareAmountKobo",
      "amountKobo",
      "priceKobo",
      "paymentAmountKobo",
      "payment.amountKobo",
      "amountPaidKobo",
    ],
    [
      "fare",
      "fareAmount",
      "amount",
      "price",
      "amountNaira",
      "paymentAmount",
      "payment.amount",
      "amountPaid",
    ],
  );

export const formatFare = (booking = {}) => formatNaira(getFareNumber(booking));

export const getTipNumber = (booking = {}) =>
  getMoneyNumber(
    booking,
    [
      "tipKobo",
      "tipAmountKobo",
      "customerTipKobo",
      "passengerTipKobo",
      "tip.amountKobo",
    ],
    [
      "tipNaira",
      "tipAmount",
      "tip",
      "customerTip",
      "passengerTip",
      "tip.amount",
      "tip.value",
    ],
  );

export const formatTip = (booking = {}) => formatNaira(getTipNumber(booking));

export const getRatingNumber = (booking = {}) => {
  const rating = getNumericFromPaths(booking, [
    "rating.value",
    "rating.score",
    "rating.rating",
    "ratingValue",
    "customerRating",
    "passengerRating",
    "review.rating",
    "customerReview.rating",
    "feedback.rating",
    "rating",
  ]);

  if (rating === null) return null;
  return Math.max(0, Math.min(5, rating));
};

export const formatRating = (booking = {}) => {
  const rating = getRatingNumber(booking);
  if (rating === null) return FALLBACK_LABEL;
  return `${Number.isInteger(rating) ? rating : rating.toFixed(1)}/5`;
};

export const getPaymentMethod = (booking = {}) =>
  toDisplayValue(
    booking.paymentMethod ||
      booking.paymentType ||
      booking.method ||
      booking.payment?.method,
  );

export const normalizeStatus = (value) => {
  const status = asText(value)?.toLowerCase().replace(/[-\s]+/g, "_");
  if (!status) return "unknown";

  if (["canceled", "cancelled_by_customer", "cancelled_by_rider", "no_show"].includes(status)) {
    return "cancelled";
  }
  if (["in_progress", "ongoing", "inprogress"].includes(status)) return "active";
  if (["finished", "delivered", "success", "succeeded", "done"].includes(status)) {
    return "completed";
  }
  if (["waiting", "awaiting", "queued"].includes(status)) return "pending";
  if (["paid", "charged", "successful"].includes(status)) return "paid";

  return status;
};

export const formatStatusLabel = (value) => {
  const normalized = normalizeStatus(value);
  if (normalized === "unknown") return FALLBACK_LABEL;

  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const formatRoute = (booking = {}) => {
  const pickup = toDisplayValue(
    booking.pickupLocationName ||
      booking.pickup?.address ||
      booking.pickup?.name ||
      booking.pickupLocation,
    UNKNOWN_LOCATION,
  );
  const dropoff = toDisplayValue(
    booking.dropoffLocationName ||
      booking.dropoff?.address ||
      booking.dropoff?.name ||
      booking.dropoffLocation,
    UNKNOWN_LOCATION,
  );

  return { pickup, dropoff };
};

// Zooma runs on West Africa Time (UTC+1, no daylight saving), so "today" means
// the Lagos calendar day whatever time zone the admin's computer is set to.
const WAT_OFFSET_MS = 60 * 60 * 1000;

const watStart = (date, unit) => {
  const shifted = new Date(date.getTime() + WAT_OFFSET_MS);
  const year = shifted.getUTCFullYear();
  const month = unit === "year" ? 0 : shifted.getUTCMonth();
  const day = unit === "day" ? shifted.getUTCDate() : 1;
  return new Date(Date.UTC(year, month, day) - WAT_OFFSET_MS);
};

export const startOfWatDay = (date = new Date()) => watStart(date, "day");

export const isWithinTimeFilter = (value, filter, now = new Date()) => {
  if (!filter || filter === "all") return true;

  const date = toDate(value);
  const currentDate = toDate(now);
  if (!date || !currentDate) return false;

  if (filter === "today") {
    return date >= watStart(currentDate, "day") && date <= currentDate;
  }
  if (filter === "month") {
    return date >= watStart(currentDate, "month") && date <= currentDate;
  }
  if (filter === "year") {
    return date >= watStart(currentDate, "year") && date <= currentDate;
  }

  return true;
};

const getBookingId = (booking = {}) => {
  const idCandidate = [booking._id, booking.id, booking.bookingId, booking.rideId]
    .map((value) => {
      if (typeof value === "object") return value?._id || value?.id;
      return value;
    })
    .find(isPresent);

  return toDisplayValue(idCandidate, "N/A");
};

const getRequestedAt = (booking) =>
  getTimestamp(booking, [
    "requestedAt",
    "requestedTime",
    "requestTime",
    "createdAt",
  ]);

const getAcceptedAt = (booking) =>
  getTimestamp(booking, [
    "timeAccepted",
    "acceptedAt",
    "acceptedTime",
    "assignedAt",
    "riderAcceptedAt",
    "driverAcceptedAt",
    "assignment.acceptedAt",
    "assignedRider.acceptedAt",
    "rider.acceptedAt",
    "driver.acceptedAt",
  ]);

const getArrivalAt = (booking) =>
  getTimestamp(booking, [
    "arrivalTime",
    "arrivedAt",
    "arrivalAt",
    "arrivalConfirmedAt",
    "arrivedTime",
    "riderArrivedAt",
    "driverArrivedAt",
    "pickupArrivedAt",
    "ride.arrivalTime",
    "ride.arrivedAt",
    "ride.arrivalAt",
    "ride.arrivedTime",
    "timestamps.arrivedAt",
    "events.arrivedAt",
    "events.arrivalAt",
  ]);

const getCompletedAt = (booking, status) => {
  const explicitCompletedAt = getTimestamp(booking, [
    "completedAt",
    "completedTime",
    "finishedAt",
    "completionTime",
  ]);
  if (explicitCompletedAt) return explicitCompletedAt;

  return status === "completed" ? booking.updatedAt || null : null;
};

const getPaidAt = (booking) =>
  getTimestamp(booking, [
    "paidAt",
    "paymentTime",
    "payment.paidAt",
    "payment.createdAt",
    "payment.timestamp",
    "transaction.paidAt",
    "transaction.createdAt",
  ]);

// The expiry job cancels unclaimed requests with this reason; they are shown as
// "Expired" rather than mixed in with cancellations people made.
export const EXPIRED_CANCEL_REASON = "expired_unclaimed";

const CANCELLED_BY_LABELS = {
  passenger: "passenger",
  driver: "rider",
  rider: "rider",
  admin: "support",
};

// Defaults the backend writes when nobody typed a reason; showing them reads as a real reason.
const PLACEHOLDER_CANCEL_REASONS = new Set(["User initiated", "Cancelled by support"]);

const CANCEL_REASON_CODES = {
  account_deactivated: "account deleted",
  no_show: "passenger didn't show up",
};

// Rows from before cancelledBy was stored: same unambiguous markers the backend uses.
const inferCancelledBy = (reason) => {
  if (reason === "no_show") return "rider";
  if (reason === "Cancelled by support") return "admin";
  return null;
};

/**
 * One line saying who cancelled a booking and why, plus when, e.g.
 * { text: "Cancelled by passenger: My plans changed", at: "2026-10-26T…" }.
 * Null when the booking isn't cancelled.
 */
export const describeCancellation = (booking = {}) => {
  if (!booking || normalizeStatus(booking.status || booking.rideStatus) !== "cancelled") {
    return null;
  }

  const reason = asText(booking.cancelReason);
  const at = booking.cancelledAt || null;

  if (reason === EXPIRED_CANCEL_REASON) {
    return { text: "Expired: no rider accepted", at: booking.expiredAt || at };
  }
  if (reason?.startsWith("abandoned_")) {
    const stage = reason.slice("abandoned_".length).replace(/_/g, " ");
    return { text: `Closed automatically: went inactive while ${stage}`, at };
  }

  const by = asText(booking.cancelledBy)?.toLowerCase() || inferCancelledBy(reason);
  const realReason =
    reason && !PLACEHOLDER_CANCEL_REASONS.has(reason)
      ? CANCEL_REASON_CODES[reason] || reason
      : null;

  if (!by) {
    return {
      text: realReason
        ? `Cancelled (who wasn't recorded): ${realReason}`
        : "Cancelled (who and why weren't recorded)",
      at,
    };
  }

  const who = by === "system" ? "automatically" : `by ${CANCELLED_BY_LABELS[by] || by}`;
  return {
    text: realReason ? `Cancelled ${who}: ${realReason}` : `Cancelled ${who} (no reason given)`,
    at,
  };
};

/** The one line shown under a cancelled ride, e.g. "Cancelled by passenger: My plans changed · 26 Oct 2026, 7:55pm". */
export const cancellationLine = (booking) => {
  const cancellation = describeCancellation(booking);
  if (!cancellation) return null;
  return cancellation.at
    ? `${cancellation.text} · ${formatTripDateTime(cancellation.at)}`
    : cancellation.text;
};

/** A typed or pasted booking ID as lowercase hex: "#BCE449B6", a full ID, or a pasted admin URL
 * (its last path segment). "" when nothing valid remains, so the caller can say so. */
export const parseBookingIdInput = (value) => {
  let text = String(value ?? "").trim();
  if (text.includes("/")) {
    const segment = text.split(/[?]/)[0].replace(/#[^/]*$/, "").split("/").filter(Boolean).pop() ?? "";
    try {
      text = decodeURIComponent(segment);
    } catch {
      text = segment;
    }
  }
  text = text.replace(/[#\s]/g, "").toLowerCase();
  return /^[0-9a-f]+$/.test(text) ? text : "";
};

/** "#" + last 8 characters, uppercase: the one short booking ID used everywhere, e.g. "#BCE449B6". */
export const shortBookingId = (id) => {
  const text = asText(typeof id === "object" ? id?._id || id?.id : id);
  return text ? `#${text.slice(-8).toUpperCase()}` : "N/A";
};

/** Naira the passenger paid (completed) or owes after any promo (not yet completed); the
 * pre-promo fare only when neither is stored; null when nothing is. */
export const getPassengerAmountNaira = (booking = {}, status) =>
  getNumericValue(status === "completed" ? booking.amountPaidNgn : booking.amountDueNgn) ??
  getNumericValue(booking.fare);

// Status badge colours for a normalized trip status (see normalizeTrip).
export const TRIP_STATUS_CLASSES = {
  requested:
    "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/30",
  pending:
    "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-200 dark:bg-yellow-400/10 dark:text-yellow-300 dark:ring-yellow-400/30",
  accepted:
    "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30",
  active:
    "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/30",
  available:
    "bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30",
  awaiting_payment:
    "bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 dark:bg-purple-400/10 dark:text-purple-300 dark:ring-purple-400/30",
  completed:
    "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30",
  cancelled:
    "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/30",
  expired:
    "bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:ring-gray-600",
  unknown:
    "bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:ring-gray-600",
  paid:
    "bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 dark:bg-green-400/10 dark:text-green-300 dark:ring-green-400/30",
};

export const normalizeTrip = (booking = {}, ridersMap = null) => {
  const baseStatus = normalizeStatus(booking.status || booking.rideStatus);
  const status =
    baseStatus === "cancelled" && booking.cancelReason === EXPIRED_CANCEL_REASON
      ? "expired"
      : baseStatus;
  const { pickup, dropoff } = formatRoute(booking);
  const id = getBookingId(booking);
  const riderSources = [
    booking.acceptedBy,
    booking.acceptedRider,
    booking.acceptedDriver,
    booking.acceptingRider,
    booking.acceptedByRider,
    booking.acceptedByDriver,
    booking.rider,
    booking.driver,
    booking.assignedRider,
    booking.riderId,
    booking.driverId,
    booking.assignedRiderId,
    booking.acceptedByRiderId,
    booking.acceptedByDriverId,
    booking.riderProfile,
  ];
  const customerSources = [
    booking.passengerId,
    booking.customer,
    booking.customerId,
    booking.user,
    booking.passenger,
  ];

  return {
    id,
    shortId: id === "N/A" ? id : shortBookingId(id),
    riderName: getPersonName(null, riderSources, "Unknown rider", ridersMap),
    customerName: getPersonName(null, customerSources, "Unknown customer"),
    pickup,
    dropoff,
    requestedAt: getRequestedAt(booking),
    acceptedAt: getAcceptedAt(booking),
    arrivalAt: getArrivalAt(booking),
    completedAt: getCompletedAt(booking, status),
    paidAt: getPaidAt(booking),
    fare: formatFare(booking),
    fareNumber: getFareNumber(booking),
    payment: formatFare(booking),
    paymentMethod: getPaymentMethod(booking),
    tip: formatTip(booking),
    tipNumber: getTipNumber(booking),
    rating: formatRating(booking),
    ratingNumber: getRatingNumber(booking),
    status,
    statusLabel: formatStatusLabel(status),
    cancellation: describeCancellation(booking),
    source: booking,
  };
};

export const getBookingRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.bookings)) return payload.bookings;
  if (Array.isArray(payload.rides)) return payload.rides;
  return [];
};

export const getPagination = (payload, fallbackPage = 1, fallbackLimit = 20) => {
  const pagination = payload?.pagination || {};
  const page = Number(pagination.page) || fallbackPage;
  const limit = Number(pagination.limit) || fallbackLimit;
  const total = Number(pagination.total) || 0;
  const totalPages = Number(pagination.totalPages) || 1;

  return {
    page,
    limit,
    total,
    totalPages,
    hasNext: Boolean(pagination.hasNext ?? page < totalPages),
    hasPrev: Boolean(pagination.hasPrev ?? page > 1),
  };
};